import React, { useState } from "react";
import { makeStyles } from "tss-react/mui";
import { mdiChevronDown } from "@mdi/js";
import { Box, Collapse, Typography } from "@mui/material";
import { MiIcon } from "../../MiIcon/MiIcon";

/**
 * MiGroupedCard component renders a collapsible card group with a title and content.
 *
 * @param {Object} props - The properties object.
 * @param {string} props.title - The title of the card group.
 * @param {React.ReactNode} props.renderCard - The content to be rendered inside the card group.
 *
 * @returns {JSX.Element} The rendered MiGroupedCard component.
 */

export const MiListGroupedCard = ({
  title,
  renderCard,
  cardProps,
  data,
  testId = "",
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const { classes, theme } = useStyles();

  const toggleGroup = () => {
    setIsExpanded((prev) => !prev);
  };

  return (
    <Box
      className={classes.outerContainer}
      data-testid={`grouped-container-${testId}`}
    >
      <Box onClick={toggleGroup} className={classes.innerContainer}>
        {typeof title === "string" ? (
          <Typography
            variant="body2"
            color="grey.dark"
            fontWeight={theme.fontWeight.semiBold}
            fontSize={theme.fontSize.small}
            lineHeight={1}
          >
            {title}
          </Typography>
        ) : (
          title
        )}
        <Box display="flex" alignItems="center" gap={1}>
          <Box
            className={classes.iconContainer}
            sx={{ transform: !isExpanded ? "rotate(180deg)" : "rotate(0deg)" }}
          >
            <MiIcon
              path={mdiChevronDown}
              size={1}
              color={theme.palette.grey.main}
              data-testid={`grouped-chevron-icon-${testId}`}
            />
          </Box>
          {cardProps?.renderGroupActions &&
            cardProps.renderGroupActions(data[0])}
        </Box>
      </Box>
      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
        {renderCard}
      </Collapse>
    </Box>
  );
};

const useStyles = makeStyles()((theme) => ({
  outerContainer: {
    width: "100%",
  },
  innerContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    backgroundColor: theme.palette.grey.lightAccent,
    borderRadius: theme.spacing(1),
    padding: theme.spacing(1, 1, 1, 2),
    border: `1px solid ${theme.palette.grey.accent}`,
    marginBottom: theme.spacing(2.5),
    cursor: "pointer",
  },
  iconContainer: {
    display: "flex",
    alignItems: "center",
    transition: "transform 0.3s ease-in-out",
  },
}));
